import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, GlassWater, ShieldCheck, Check, ExternalLink, Globe2, LineChart, HeartPulse } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';

const PROJECTS = [
  {
    id: 'midzon',
    tag: 'E-Commerce & Retail',
    name: 'Midzon',
    icon: ShoppingCart,
    accent: '#0a6cb0',
    headline: 'Brand Creation & Full-Stack E-Commerce Development',
    impact:
      'We built Midzon from the ground up, establishing it as a premier e-commerce platform and a leading retail/wholesale supershop in Bangladesh. Our technology team architected the entire digital storefront, ensuring seamless user experience, scalable inventory management, and a robust digital identity that perfectly connects the online platform with their physical wholesale operations.',
    chips: ['Full-stack build', 'Inventory at scale', 'Omnichannel', 'Brand identity'],
    stats: [
      { label: 'Market', value: 'Bangladesh' },
      { label: 'Scope', value: 'Platform + Brand' },
      { label: 'Model', value: 'Retail / Wholesale' },
    ],
  },
  {
    id: 'ayan',
    tag: 'Food & Beverage',
    name: 'Ayan Bevande',
    icon: GlassWater,
    accent: '#0a86d4',
    headline: 'Corporate Website & Digital Identity',
    impact:
      'As a dynamic and growing beverage company, Ayan Bevande needed a digital presence as refreshing as their products. Midbrothers engineered and launched their complete website, providing a modern, high-performing digital storefront that elevates their brand authority and connects them directly with their target market.',
    chips: ['Corporate site', 'Design system', 'Performance', 'Brand voice'],
    stats: [
      { label: 'Industry', value: 'Beverages' },
      { label: 'Scope', value: 'Web + Identity' },
      { label: 'Delivery', value: 'End-to-end' },
    ],
  },
  {
    id: 'insurance',
    tag: 'Financial Security',
    name: 'Comprehensive Insurance Solutions',
    icon: ShieldCheck,
    accent: '#0f7a5a',
    headline: 'Client Wealth & Health Security',
    impact:
      'Beyond enterprise technology, we believe in securing the human element. Midbrothers has successfully navigated the complex financial landscape to help countless happy clients secure their futures. We have actively managed and facilitated tailored Life Insurance, Health Insurance, and diverse risk-management portfolios, ensuring our clients can innovate with peace of mind.',
    chips: ['Life Insurance', 'Health Insurance', 'Risk Management', 'Wealth Planning'],
    stats: [
      { label: 'Focus', value: 'Individuals & Families' },
      { label: 'Services', value: 'Life, Health, Portfolio' },
      { label: 'Approach', value: 'Tailored & Advisory' },
    ],
  },
];

const Portfolio = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2c4d] via-[#0a2c4d] to-[#0a3b6a]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #0a6cb0 0%, transparent 50%), radial-gradient(circle at 80% 70%, #0a6cb0 0%, transparent 50%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-32">
          <p className="eyebrow text-white/70 mb-4">OUR BUILDS &amp; PARTNERSHIPS</p>
          <h1 className="font-serif-display text-white text-5xl md:text-6xl font-bold leading-[1.05] mb-6 max-w-4xl">
            Empowering global brands with world-class technology and financial infrastructure.
          </h1>
          <p className="text-white/80 text-lg leading-relaxed max-w-3xl mb-10">
            From scaling e-commerce platforms to securing individual futures, see how Midbrothers turns
            outside-the-box solutions into tangible results.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { icon: Globe2, label: 'Markets', value: '15+' },
              { icon: LineChart, label: 'Projects', value: '40+' },
              { icon: HeartPulse, label: 'Happy clients', value: '1,200+' },
              { icon: ShieldCheck, label: 'Years', value: '15+' },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/15 text-white">
                <s.icon size={18} className="mb-2 text-white/80" />
                <p className="text-2xl font-bold font-serif-display">{s.value}</p>
                <p className="text-xs uppercase tracking-wider text-white/70">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-24 bg-[#f6f9fc]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">CASE STUDIES</p>
            <h2 className="section-title text-4xl md:text-5xl">Projects That Define Us</h2>
          </div>

          <div className="space-y-10">
            {PROJECTS.map((p, idx) => {
              const Icon = p.icon;
              const reverse = idx % 2 === 1;
              return (
                <article
                  key={p.id}
                  className="grid lg:grid-cols-5 gap-8 items-stretch bg-white rounded-2xl border border-[#eef1f5] shadow-[0_10px_30px_-20px_rgba(10,44,77,0.2)] overflow-hidden"
                >
                  {/* Visual side */}
                  <div
                    className={`lg:col-span-2 relative p-10 flex flex-col justify-between ${reverse ? 'lg:order-last' : ''}`}
                    style={{ background: `linear-gradient(135deg, ${p.accent} 0%, #0a2c4d 100%)` }}
                  >
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #ffffff 0%, transparent 50%)' }} />
                    <div className="relative text-white">
                      <span className="inline-block text-[11px] uppercase tracking-widest bg-white/15 px-3 py-1 rounded-full mb-6">{p.tag}</span>
                      <div className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-6 border border-white/20">
                        <Icon size={38} />
                      </div>
                      <h3 className="font-serif-display text-4xl md:text-5xl font-bold leading-tight">{p.name}</h3>
                    </div>
                    <div className="relative grid grid-cols-3 gap-3 mt-10">
                      {p.stats.map((s) => (
                        <div key={s.label} className="text-white">
                          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">{s.label}</p>
                          <p className="text-sm font-semibold">{s.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Content side */}
                  <div className="lg:col-span-3 p-10 flex flex-col">
                    <p className="eyebrow mb-3">The Project</p>
                    <h4 className="font-serif-display text-2xl md:text-3xl font-bold text-[#0a2c4d] mb-5 leading-tight">
                      {p.headline}
                    </h4>
                    <p className="eyebrow mb-2 text-[#8ea1b8]">The Impact</p>
                    <p className="text-[#556a85] text-[16px] leading-relaxed mb-6">{p.impact}</p>

                    <div className="flex flex-wrap gap-2 mb-7">
                      {p.chips.map((c) => (
                        <span key={c} className="inline-flex items-center gap-2 text-xs font-semibold text-[#0a6cb0] bg-[#eaf3fb] px-3 py-1.5 rounded-full">
                          <Check size={12} /> {c}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto flex items-center gap-5">
                      <a href="#contact" className="inline-flex items-center gap-2 text-[#0a6cb0] font-semibold text-sm uppercase tracking-wider group">
                        Discuss a similar project <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capability band */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">WHAT WE DELIVER</p>
            <h2 className="section-title text-3xl md:text-4xl">End-to-end capability, under one roof</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: ShoppingCart, title: 'Digital Platforms', desc: 'E-commerce, marketplaces, and enterprise web apps engineered to scale.' },
              { icon: ShieldCheck, title: 'Financial Advisory', desc: 'Wealth, insurance, and risk-management portfolios tailored to each client.' },
              { icon: Globe2, title: 'Brand &amp; Identity', desc: 'Corporate websites and design systems that make brands feel inevitable.' },
            ].map((c) => (
              <div key={c.title} className="service-card rounded-xl p-8">
                <div className="service-icon-wrap mb-5"><c.icon size={24} /></div>
                <h3 className="service-title font-bold text-[#0a2c4d] text-lg mb-2" dangerouslySetInnerHTML={{ __html: c.title }} />
                <p className="service-desc text-[#556a85] text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: c.desc }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection />

      {/* CTA Banner */}
      <section className="relative py-20 bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2c4d] via-[#0a2c4d]/95 to-[#0a3b6a]" />
        <div className="relative max-w-[1080px] mx-auto px-6 text-center">
          <h2 className="font-serif-display text-white text-3xl md:text-4xl font-bold mb-4">
            Have a project in mind?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
            Tell us what you&apos;re building. We&apos;ll bring the blueprint and the build.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#contact" className="pill-btn">
              START A CONVERSATION <ArrowRight size={18} />
            </a>
            <Link to="/retirement" className="pill-btn pill-btn-outline" style={{ background: 'transparent', color: '#ffffff', borderColor: '#ffffff' }}>
              EXPLORE SOLUTIONS <ExternalLink size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;
